/**
 * Maturity Assessment Questions - Single Source of Truth
 * 
 * This file contains the canonical definition of all maturity assessment questions.
 * All other components should import from this file to maintain consistency.
 */

export interface MaturityQuestion {
  section: "Operational Excellence" | "Strategic Alignment";
  title: string;
  options: string[];
}

export const sections = [
  { name: "Operational Excellence", start: 0, end: 4 },
  { name: "Strategic Alignment", start: 5, end: 10 }
];

export const maturityQuestions: MaturityQuestion[] = [
  // Section 1: Operational Excellence (0-4)
  {
    section: "Operational Excellence",
    title: "Account reconciliation",
    options: [
      "Manual spreadsheets, fragmented controls - All reconciliations are performed manually using spreadsheets. Limited or no controls in place. High risk of errors and significant manual effort required.",
      "Basic automation, manual oversight - Some reconciliation software is being used. Financial data is downloaded automatically to a reconciliation database. Adjustments are partially automated but still require manual analysis and monitoring.",
      "Partial automation, limited SAP integration - Standardized processes with moderate automation and consistent controls. Some integration with core ERP systems but gaps remain in automation coverage.",
      "80% automated, real-time exception alerts - High degree of automation with integrated workflows and proactive monitoring. Most reconciliations are automated with real-time exception detection and alerts.",
      "Touchless, fully automated with real-time SAP data - Full automation with real-time processing and touchless operations. Complete integration with ERP systems and autonomous reconciliation processes."
    ]
  },
  {
    section: "Operational Excellence",
    title: "Journal entries",
    options: [
      "Manual prep and posting - All journal entries are created and posted manually. No standardized templates or automation. High manual effort and error risk.",
      "Templates with some validations - Basic digital approvals implemented, but journal creation and posting remain largely manual processes. Some standardized templates are used.",
      "Recurring journals partially automated - Standard recurring journal entries are partially automated. Manual intervention still required for non-standard entries and complex scenarios.",
      "Rule-based journal automation - Advanced rule-based automation for most journal entries. Intelligent routing and approval workflows with minimal manual intervention required.",
      "Touchless journal orchestration - Complete automation of journal entry creation, validation, and posting. AI-driven intelligent processing with full audit trails and exception handling."
    ]
  },
  {
    section: "Operational Excellence",
    title: "Accruals and provisions",
    options: [
      "Offline estimation, Excel-based - All accruals and provisions are calculated manually using Excel spreadsheets. No automated calculations or validation rules in place.",
      "Templates and trends - Standardized Excel templates are used with basic trend analysis. Some historical data is leveraged but calculations remain largely manual.",
      "Standard rules partially automated - More than 90% of accruals are automatically calculated and posted with some manual oversight. Standard business rules are implemented in the system.",
      "Data-driven rules engine - Advanced rules engine automatically calculates accruals based on real-time data. Sophisticated algorithms and predictive analytics are used for accuracy.",
      "AI-powered autonomous provisioning - Fully automated accrual and provision calculations with artificial intelligence. Machine learning algorithms continuously improve accuracy and prediction capabilities."
    ]
  },
  {
    section: "Operational Excellence", 
    title: "Intercompany transactions",
    options: [
      "Manual clearing, spreadsheet-based - All intercompany transactions are tracked and reconciled manually using spreadsheets. High manual effort and reconciliation challenges across entities.",
      "Spreadsheets with basic validations - Manual initiation of intercompany transactions with basic reconciliation tools in place. Some automated validations but largely manual processes.",
      "Automated matching with exception handling - Automated intercompany matching and clearing processes with systematic exception handling. Manual intervention required only for complex scenarios.",
      "Real-time clearing with intelligent routing - Advanced real-time processing of intercompany transactions with intelligent routing and automated clearing. Minimal manual intervention required.",
      "Autonomous intercompany orchestration - Fully autonomous intercompany transaction processing with AI-driven optimization. Complete automation from initiation to final clearing and reporting."
    ]
  },
  {
    section: "Operational Excellence",
    title: "Close checklist and task management",
    options: [
      "Paper-based or email coordination - Financial close activities are coordinated through manual methods like paper checklists or email communications. No centralized tracking or automation.",
      "Standalone close checklist - Digital checklist tools are used but operate in isolation. Limited integration with other systems and minimal automation of task progression.",
      "Integrated workflow management - Comprehensive workflow management system with integrated task tracking, dependencies, and automated progression. Real-time visibility into close status.",
      "Intelligent task orchestration - Advanced orchestration with intelligent task sequencing, resource optimization, and predictive timeline management. Automated bottleneck identification and resolution.",
      "Autonomous close management - Fully autonomous close management with AI-driven optimization, self-healing workflows, and continuous process improvement without human intervention."
    ]
  },
  // Section 2: Strategic Alignment (5-10) - UPDATED WITH NEW CONDENSED TEXT
  {
    section: "Strategic Alignment",
    title: "Exception handling and escalation",
    options: [
      "Ad-hoc manual resolution - Exceptions are handled reactively on a case-by-case basis. No standardized procedures or systematic escalation processes in place.",
      "Basic escalation procedures - Simple escalation procedures are defined with basic categorization of exceptions. Manual routing and resolution with limited automation.",
      "Structured exception workflows - Well-defined exception workflows with automated routing based on type and severity. Systematic escalation procedures with tracking and reporting.",
      "Intelligent exception routing - Advanced exception management with AI-powered categorization and intelligent routing. Automated resolution for routine exceptions with smart escalation.",
      "AI-driven predictive exception management - Predictive exception management using machine learning to prevent issues before they occur. Autonomous resolution with continuous learning and optimization."
    ]
  },
  {
    section: "Strategic Alignment",
    title: "Automation coverage across R2R processes",
    options: [
      "Limited or no automation - Minimal automation across R2R processes. Most activities are performed manually with basic tools and spreadsheets.",
      "Basic automation in key areas - Some automation implemented in specific high-volume or critical areas. Isolated solutions with limited integration across the process.",
      "Moderate automation coverage - Systematic automation across multiple R2R processes with integrated workflows. Significant reduction in manual effort but gaps remain.",
      "Comprehensive automation strategy - Extensive automation coverage across most R2R processes with integrated technology platform. Strategic approach to continuous automation expansion.",
      "Full automation with continuous optimization - Complete automation across all R2R processes with AI-driven optimization. Continuous improvement and adaptation based on performance analytics."
    ]
  },
  {
    section: "Strategic Alignment",
    title: "Process standardization and documentation",
    options: [
      "Processes are mostly manual and vary between teams or regions; documentation is inconsistent and audit trails often need to be reconstructed after the close, causing delays and rework.",
      "Some digital controls and templates are in place, but workflows still differ across teams/regions, leading to variability in execution and reduced process efficiency.",
      "A central governance framework exists with defined process documentation, but enforcement and adherence to these standards are inconsistent across the organization.",
      "Automated workflows consistently enforce segregation of duties, approval policies, and capture of audit evidence; process documentation is actively maintained and applied.",
      "Proactive governance with embedded compliance checkpoints, automated audit evidence capture, and real-time visibility into risk/control performance across all entities."
    ]
  },
  {
    section: "Strategic Alignment",
    title: "Technology stack integration",
    options: [
      "Automation is implemented through isolated, tool-specific solutions aimed at improving individual tasks, without integration into the broader R2R process flow.",
      "Some cross-functional collaboration exists between teams, but integration remains limited and automation often addresses only localized pain points.",
      "A documented strategy links multiple automation initiatives, though integration gaps persist between systems, entities, or process areas.",
      "End-to-end orchestration connects all major R2R processes and aligns automation design with enterprise finance strategy and objectives.",
      "Automation is platform-led, scalable, and deeply integrated across entities, functions, and systems, supported by proactive governance and continuous optimization."
    ]
  },
  {
    section: "Strategic Alignment", 
    title: "Data integrity and validation",
    options: [
      "Exceptions are addressed reactively, often discovered after deadlines, leading to manual rework, missed SLAs, and increased risk of reporting errors.",
      "Workarounds and local escalation paths are used inconsistently, resulting in duplicated effort and variable resolution quality across the organization.",
      "Basic rules, triggers, and controls exist for exception detection, but responses are not standardized, and monitoring is inconsistent between processes.",
      "Automated detection identifies exceptions in real time and provides guided resolution workflows embedded directly within core processes.",
      "Exception management is fully automated and predictive where possible, with real-time alerts, escalation workflows, and comprehensive reporting built into orchestration platforms."
    ]
  },
  {
    section: "Strategic Alignment",
    title: "Metrics and continuous improvement",
    options: [
      "No formal KPIs for R2R automation; benefits are anecdotal, improvements are not measured, and success is based on subjective perception.",
      "High-level metrics such as close cycle duration and error counts are tracked, but improvements are not directly attributed to automation efforts.",
      "KPIs include touchless completion rates, days to eliminate intercompany balances, and SLA breach counts; metrics provide partial insight into automation performance.",
      "End-to-end process KPIs are tracked consistently with regular review cycles and clearly defined improvement targets at both entity and group levels.",
      "Metrics and analytics are deeply integrated into finance operations, using predictive insights to proactively identify optimization opportunities and measure automation maturity gains."
    ]
  }
];

// Helper function to get strategic alignment questions only
export const getStrategicAlignmentQuestions = (): MaturityQuestion[] => {
  return maturityQuestions.filter(q => q.section === "Strategic Alignment");
};

// Helper function to get operational excellence questions only  
export const getOperationalExcellenceQuestions = (): MaturityQuestion[] => {
  return maturityQuestions.filter(q => q.section === "Operational Excellence");
};

// Export legacy questions format for backward compatibility
export const questions = maturityQuestions;